import { Eye, LogOut } from 'lucide-react';
import { firebaseAuth } from '@/lib/firebase';
import { useAuth } from '@/lib/auth';
import { useImpersonation } from '@/hooks/useImpersonation';
import { signOut } from 'firebase/auth';

/**
 * ImpersonationBanner — global "view as user" marker.
 *
 * Rendered once at app-shell level (see App.tsx AppWrapper), ABOVE every route
 * and layout, so no page can escape it while an admin is impersonating a user.
 *
 * Like GlobalTrialBanner, this is an INLINE, flex-shrink-0 block at the top of
 * the shell's flex column — NOT position:fixed. Fixed positioning overlaps the
 * app's sticky h-16 navbars (documented in TrialBanner.tsx); an inline block
 * pushes all content — navbars included — down by its own height, which is the
 * clean, non-overlapping behavior we want.
 *
 * Visibility is driven purely by the Firebase impersonation claim via
 * useImpersonation — no sessionStorage/localStorage, so it survives refresh and
 * cannot be spoofed client-side.
 */
export function ImpersonationBanner() {
  const { isImpersonating } = useImpersonation();
  const { user } = useAuth();

  if (!isImpersonating) return null;

  const handleExit = async () => {
    try {
      await signOut(firebaseAuth);
    } catch {
      // Ignore — fall through to a hard redirect either way so the impersonated
      // session (and this banner) is fully torn down.
    }
    // Hard navigation to the login route guarantees a clean state reset,
    // matching the app's own sign-out behavior.
    window.location.href = '/auth';
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full flex-shrink-0 bg-amber-400 text-amber-950 border-b border-amber-500 shadow-sm"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span className="text-sm font-semibold truncate">
            Admin view — signed in as {user?.email ?? 'this user'}.{' '}
            <span className="font-bold">Look, don&apos;t touch.</span>
          </span>
        </div>

        <button
          type="button"
          onClick={handleExit}
          className="flex-shrink-0 inline-flex items-center gap-1.5 min-h-[44px] min-w-[44px] justify-center rounded-md bg-amber-950/10 px-3 py-1.5 text-sm font-semibold text-amber-950 transition-colors hover:bg-amber-950/20 focus:outline-none focus:ring-2 focus:ring-amber-950 focus:ring-offset-1 focus:ring-offset-amber-400"
          aria-label="Exit admin view and return to sign in"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          <span>Exit</span>
        </button>
      </div>
    </div>
  );
}

export default ImpersonationBanner;
